import {
  AdminAnalyticsKpiCard,
  AdminAssessmentStatsChart,
  AdminCoursePerformanceChart,
  AdminDashboardChartCard,
  AdminEngagementOverviewChart,
  AdminLeaderboardPreview,
  useTheme,
} from '@e-learning/shared';
import { useTranslation } from 'react-i18next';
import assessmentDashboardIcon from '../../../../../shared/assets/dashboard/assessment-dashboard.png';
import courseDashboardIcon from '../../../../../shared/assets/dashboard/course-dashboard.png';
import engagementDashboardIcon from '../../../../../shared/assets/dashboard/engagement-dashboard.png';
import leaderboardDashboardIcon from '../../../../../shared/assets/dashboard/leaderboard-dashboard.png';
import {
  useAdminDashboardAnalyticsKpis,
  useAdminDashboardManagementWidgets,
} from './hooks';
export default function Dashboard() {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const prefix = 'admin_dashboard.analytics';
  const chartPrefix = 'admin_dashboard.charts';

  const { loading, error, cards } = useAdminDashboardAnalyticsKpis();
  const widgets = useAdminDashboardManagementWidgets();

  const chartIconSize = 24;

  const chartHeaderIcon = (src: string) => (
    <img
      src={src}
      alt=""
      width={chartIconSize}
      height={chartIconSize}
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );

  const chartsGridStyle = {
    display: 'grid',
    gridTemplateColumns:
      theme.palette.adminDashboard.charts.gridTemplateColumns,
    gap: theme.palette.adminDashboard.charts.gridGap,
    width: '100%',
    marginTop: theme.palette.adminDashboard.charts.pageSectionMarginTop,
    alignItems: 'stretch',
  } as const;

  return (
    <div
      style={{
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing.lg,
      }}
    >
      <h1
        style={{
          marginBottom: theme.spacing.xs,
          fontWeight: theme.typography.fontWeight.semibold,
          color: theme.palette.text.primary,
          fontSize: theme.typography.fontSize['2xl'],
        }}
      >
        {t(`${prefix}.title`)}
      </h1>
      <p
        style={{
          color: theme.palette.text.secondary,
          marginBottom: theme.spacing.xl,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {t(`${prefix}.subtitle`)}
      </p>

      {loading && (
        <p
          style={{
            color: theme.palette.text.secondary,
            margin: 0,
          }}
        >
          {t(`${prefix}.loading`)}
        </p>
      )}

      {error && !loading && (
        <p
          style={{
            color: theme.palette.error.main,
            margin: 0,
          }}
        >
          {t(`${prefix}.load_error`)}
        </p>
      )}

      {!loading && !error && cards.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.palette.adminDashboard.analyticsKpi.cardGridGap,
            width: '100%',
          }}
        >
          {cards.map(card => (
            <AdminAnalyticsKpiCard
              key={card.labelKey}
              variant={card.variant}
              label={t(card.labelKey)}
              value={card.value}
              trend={card.trend}
              trendTone={card.trendTone}
            />
          ))}
        </div>
      )}

      <div style={chartsGridStyle}>
        <AdminDashboardChartCard
          title={t(`${chartPrefix}.engagement.title`)}
          subtitle={t(`${chartPrefix}.engagement.subtitle`)}
          headerIcon={chartHeaderIcon(engagementDashboardIcon)}
        >
          <AdminEngagementOverviewChart
            points={widgets.engagementPoints}
            legendTotalLabel={t(`${chartPrefix}.engagement.legend_total`)}
            legendActiveLabel={t(`${chartPrefix}.engagement.legend_active`)}
            loadingLabel={t(`${chartPrefix}.loading`)}
            emptyLabel={t(`${chartPrefix}.engagement.empty`)}
            errorLabel={t(`${chartPrefix}.engagement.error`)}
            loading={widgets.loading}
            error={widgets.engagementError}
          />
        </AdminDashboardChartCard>

        <AdminDashboardChartCard
          title={t(`${chartPrefix}.course_performance.title`)}
          subtitle={t(`${chartPrefix}.course_performance.subtitle`)}
          headerIcon={chartHeaderIcon(courseDashboardIcon)}
        >
          <AdminCoursePerformanceChart
            rows={widgets.courseRows}
            legendCompletionLabel={t(
              `${chartPrefix}.course_performance.legend_completion`
            )}
            legendInProgressLabel={t(
              `${chartPrefix}.course_performance.legend_in_progress`
            )}
            loadingLabel={t(`${chartPrefix}.loading`)}
            emptyLabel={t(`${chartPrefix}.course_performance.empty`)}
            errorLabel={t(`${chartPrefix}.course_performance.error`)}
            loading={widgets.loading}
            error={widgets.courseError}
          />
        </AdminDashboardChartCard>

        <AdminDashboardChartCard
          title={t(`${chartPrefix}.assessment.title`)}
          subtitle={t(`${chartPrefix}.assessment.subtitle`)}
          headerIcon={chartHeaderIcon(assessmentDashboardIcon)}
        >
          <AdminAssessmentStatsChart
            passedPercent={widgets.assessmentStats?.passedAttemptsPercent ?? 0}
            failedPercent={widgets.assessmentStats?.failedAttemptsPercent ?? 0}
            pendingPercent={
              widgets.assessmentStats?.pendingUserCoursesPercent ?? 0
            }
            labelPassed={t(`${chartPrefix}.assessment.passed`)}
            labelFailed={t(`${chartPrefix}.assessment.failed`)}
            labelPending={t(`${chartPrefix}.assessment.pending`)}
            loadingLabel={t(`${chartPrefix}.loading`)}
            errorLabel={t(`${chartPrefix}.assessment.error`)}
            loading={widgets.loading}
            error={widgets.assessmentError}
          />
        </AdminDashboardChartCard>

        <AdminDashboardChartCard
          title={t(`${chartPrefix}.leaderboard.title`)}
          subtitle={t(`${chartPrefix}.leaderboard.subtitle`)}
          headerIcon={chartHeaderIcon(leaderboardDashboardIcon)}
        >
          <AdminLeaderboardPreview
            items={widgets.leaderboardItems.slice(0, 5).map(it => ({
              rank: it.rank,
              name: it.name,
              pointsLabel: t(`${chartPrefix}.leaderboard.xp`, {
                points: it.activePoints,
              }),
            }))}
            loadingLabel={t(`${chartPrefix}.loading`)}
            emptyLabel={t(`${chartPrefix}.leaderboard.empty`)}
            errorLabel={t(`${chartPrefix}.leaderboard.error`)}
            loading={widgets.loading}
            error={widgets.leaderboardError}
          />
        </AdminDashboardChartCard>
      </div>
    </div>
  );
}
